import type { TFunction } from 'i18next';
import {
  SCHEDULER_SECOND_SHIFT_MAX_MINUTES,
  SCHEDULER_SECOND_SHIFT_MIN_MINUTES,
  SCHEDULER_SECOND_SHIFT_STEP_MINUTES,
  type SchedulerAlertDto,
  type SchedulerRescueAffectedDto,
  type SchedulerRescueFinishDto,
  type SchedulerRescueLeaveDto,
  type SchedulerRescuePlanDto,
  type SchedulerRescueProblemDto,
  type SchedulerRescueStepDto,
  type SchedulerRescueStepInput,
  type SchedulerTeamDto,
  type SchedulerWhatIfRequestInput,
} from '@kreart/shared';
import { formatDate, formatDateTime, formatMinutes } from './components/scheduling-form';

/**
 * Pure helpers for the deadline recommendations ("Recomandare pentru termen").
 *
 * The server works out one recommendation per at-risk project and hangs it on
 * that project's PROJECT_AT_RISK alert (`alert.rescue`). Everything the web does
 * with it — find the one for a Gantt row or a board column, word it, turn the
 * manager's choices back into the steps the preview and "Aplică" send — lives
 * here, so the notification strip, the Gantt, the board and the dialog all say
 * the same thing about the same plan.
 */

/** How a stage code is named in the UI — the page's `stageLabel`. */
export type RescueStageLabel = (code: string) => string;

/** One recommendation together with the alert that carries it. */
export interface RescueEntry {
  alert: SchedulerAlertDto;
  plan: SchedulerRescuePlanDto;
}

const key = (name: string): string => `operationsScheduler.rescue.${name}`;

/** Fallback for callers that have no `stageLabel` of their own to pass. */
function defaultStageLabel(t: TFunction): RescueStageLabel {
  return (code) => t(`operationsScheduler.stages.${code}`, { defaultValue: code });
}

/**
 * Every recommendation on the board, by the scheduler row it is for — whatever
 * its status, so a Gantt row can also say "still working it out" (PENDING) or
 * "nothing helps" (NONE). Keyed by `schedulerProjectId`, never by the alert's
 * `projectId`: a warranty intervention shares the latter with its production run.
 */
export function rescueByProject(alerts: readonly SchedulerAlertDto[]): Map<string, RescueEntry> {
  const byProject = new Map<string, RescueEntry>();
  for (const alert of alerts) {
    const plan = alert.rescue;
    if (!plan || byProject.has(plan.schedulerProjectId)) continue;
    byProject.set(plan.schedulerProjectId, { alert, plan });
  }
  return byProject;
}

/**
 * The READY recommendations by the operations they act on
 * (`plan.targetStageIds`) — where the board's columns show them. An operation
 * belongs to one project, so it is never claimed by two plans.
 */
export function rescueByStage(alerts: readonly SchedulerAlertDto[]): Map<string, RescueEntry> {
  const byStage = new Map<string, RescueEntry>();
  for (const alert of alerts) {
    const plan = alert.rescue;
    if (!plan || plan.status !== 'READY') continue;
    for (const stageId of plan.targetStageIds) {
      if (!byStage.has(stageId)) byStage.set(stageId, { alert, plan });
    }
  }
  return byStage;
}

// --------------------------------------------------------------------------- //
// The manager's choices → the steps sent back                                  //
// --------------------------------------------------------------------------- //

/** Who a collaborator step brings in: an existing COLLABORATOR crew, or a new one. */
export type RescueCollaboratorChoice = { teamId: string } | { create: { name?: string } };

/**
 * What the apply dialog lets the manager change before "Aplică". Without any,
 * the steps go back exactly as recommended (a collaborator step as the generic
 * "a collaborator", which only the preview accepts).
 */
export interface RescueEdits {
  /** Indexes into `plan.steps` the manager un-ticked. */
  excluded?: ReadonlySet<number>;
  /** Per collaborator step, by its `stageId`: the crew picked or the one to create. */
  collaborators?: Record<string, RescueCollaboratorChoice>;
  /** The second shift in hours a day, as typed (4–8); rounded to the half hour. */
  secondShiftHours?: number;
}

/**
 * A second shift typed in hours, as the minutes the API takes: on the
 * half-hour grid the server (and the database) enforce, so 4.25 goes as 4.5 h.
 */
export function rescueShiftMinutes(hours: number): number {
  return Math.round(hours * 2) * SCHEDULER_SECOND_SHIFT_STEP_MINUTES;
}

/** 4 to 8 hours: the least a second shift is worth organising, the most one crew can add. */
export function rescueShiftHoursValid(hours: number): boolean {
  return (
    Number.isFinite(hours) &&
    hours * 60 >= SCHEDULER_SECOND_SHIFT_MIN_MINUTES &&
    hours * 60 <= SCHEDULER_SECOND_SHIFT_MAX_MINUTES
  );
}

/** A new collaborator's name as sent: trimmed, and left out when empty (the server picks one). */
function collaboratorInput(
  choice: RescueCollaboratorChoice | undefined,
): { teamId: string } | { create: { name?: string } } | null {
  if (!choice) return null;
  if ('teamId' in choice) return { teamId: choice.teamId };
  const name = choice.create.name?.trim();
  return { create: name ? { name } : {} };
}

/**
 * A recommendation as the steps the preview (`what-if` with `rescue`) and
 * "Aplică" send back, with the manager's edits applied: un-ticked steps left
 * out, the collaborator picked (else the generic one — `collaborator: null`),
 * the second shift in minutes.
 */
export function rescueStepInputs(
  plan: SchedulerRescuePlanDto,
  edits: RescueEdits = {},
): SchedulerRescueStepInput[] {
  const inputs: SchedulerRescueStepInput[] = [];
  plan.steps.forEach((step, index) => {
    if (edits.excluded?.has(index)) return;
    if (step.kind === 'CREW') {
      inputs.push({
        kind: 'CREW',
        stageId: step.stageId,
        teamId: step.teamId,
        mode: step.mode,
        // The source is what a move is re-checked against; the schema refuses
        // one on the other modes, so it is only ever sent with MOVE_NOW.
        fromStageId: step.mode === 'MOVE_NOW' ? step.fromStageId : null,
      });
    } else if (step.kind === 'COLLABORATOR') {
      inputs.push({
        kind: 'COLLABORATOR',
        stageId: step.stageId,
        collaborator: collaboratorInput(edits.collaborators?.[step.stageId]),
      });
    } else {
      inputs.push({
        kind: 'SECOND_SHIFT',
        stageId: step.stageId,
        minutesPerDay:
          edits.secondShiftHours === undefined
            ? step.minutesPerDay
            : rescueShiftMinutes(edits.secondShiftHours),
      });
    }
  });
  return inputs;
}

/**
 * The what-if body that previews a recommendation — on its own, as the server
 * requires (it is measured against the board as it is). Callers make sure at
 * least one step is left: the schema refuses an empty list.
 */
export function rescueWhatIfBody(
  plan: SchedulerRescuePlanDto,
  edits?: RescueEdits,
): SchedulerWhatIfRequestInput {
  return {
    rescue: { schedulerProjectId: plan.schedulerProjectId, steps: rescueStepInputs(plan, edits) },
  };
}

/** Where an existing collaborator stands, as the apply dialog's picker shows it. */
export type RescueCollaboratorState = 'FREE' | 'BUSY' | 'INACTIVE' | 'RESERVED';

export interface RescueCollaboratorOption {
  team: SchedulerTeamDto;
  /**
   * FREE — active and idle; BUSY — on another job, joins when it is done;
   * INACTIVE — reactivated by "Aplică"; RESERVED — held for another operation
   * until that one is done, so it cannot be picked.
   */
  state: RescueCollaboratorState;
}

/**
 * The COLLABORATOR crews that could take a collaborator step: those covering
 * its operation, by name. Internal crews are never offered — a collaborator
 * step is precisely the external help the shop's own crews could not give.
 */
export function rescueCollaboratorOptions(
  teams: readonly SchedulerTeamDto[],
  step: { stageId: string; stageCode: string },
): RescueCollaboratorOption[] {
  return teams
    .filter(
      (team) =>
        team.kind === 'COLLABORATOR' &&
        (team.stageCode === step.stageCode ||
          (team.extraStageCodes as readonly string[]).includes(step.stageCode)),
    )
    .sort((a, b) => a.name.localeCompare(b.name, 'ro', { numeric: true }))
    .map((team) => ({
      team,
      state:
        team.reservedFor && team.reservedFor.stageId !== step.stageId
          ? 'RESERVED'
          : !team.active
            ? 'INACTIVE'
            : team.assignment
              ? 'BUSY'
              : 'FREE',
    }));
}

/**
 * The collaborator preselected for a step: the first free one (it starts
 * straight away), else the first inactive one (reactivated, also free), else
 * a new one — never a busy crew, which would only join once its job is done,
 * and never one in `exclude`: already preselected for another step of the same
 * plan, since one crew is reserved for one operation at a time.
 */
export function defaultRescueCollaborator(
  options: readonly RescueCollaboratorOption[],
  exclude: ReadonlySet<string> = new Set(),
): RescueCollaboratorChoice {
  const open = options.filter((option) => !exclude.has(option.team.id));
  const pick =
    open.find((option) => option.state === 'FREE') ??
    open.find((option) => option.state === 'INACTIVE');
  return pick ? { teamId: pick.team.id } : { create: {} };
}

// --------------------------------------------------------------------------- //
// Wording                                                                      //
// --------------------------------------------------------------------------- //

/** A `YYYY-MM-DD` calendar day (leave), formatted like `formatDate` — as the day it names. */
export function formatCalendarDay(date: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return '—';
  // Read and printed in UTC: the day is a date, not an instant, so no
  // timezone may move it across midnight.
  return new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium', timeZone: 'UTC' }).format(
    new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))),
  );
}

/** "on time" / "~3h late" / "no date" — one side of a before → after. */
function statusText(finish: SchedulerRescueFinishDto, t: TFunction): string {
  if (finish.blocked || finish.projectedFinishAt === null) return t(key('status.unknown'));
  return (finish.lateByMinutes ?? 0) > 0
    ? t(key('status.late'), { lateBy: formatMinutes(finish.lateByMinutes ?? 0) })
    : t(key('status.onTime'));
}

/**
 * Where a project ends up, for the result line: "2 oct. 2026, 14:00 (~3h peste
 * termen)", or "fără dată" while an operation has no crew at all.
 */
export function rescueFinishText(finish: SchedulerRescueFinishDto, t: TFunction): string {
  if (finish.blocked || finish.projectedFinishAt === null) return t(key('status.unknown'));
  return `${formatDateTime(finish.projectedFinishAt)} (${statusText(finish, t)})`;
}

/** The one-line form of a step, for the summary ("echipa A2 și la Asamblare, acum"). */
export function rescueShortStepText(
  step: SchedulerRescueStepDto,
  t: TFunction,
  stageLabel: RescueStageLabel,
): string {
  const stage = stageLabel(step.stageCode);
  if (step.kind === 'CREW') {
    const name =
      step.mode === 'JOIN_NOW'
        ? 'short.crewNow'
        : step.mode === 'PLANNED'
          ? 'short.crewPlanned'
          : 'short.crewMove';
    return t(key(name), { team: step.teamName, stage });
  }
  if (step.kind === 'COLLABORATOR') return t(key('short.collaborator'), { stage });
  return t(key('short.shift'), { hours: formatMinutes(step.minutesPerDay), stage });
}

/**
 * Why there is no recommendation (NONE), naming the project or the operation
 * the reason is about.
 */
export function rescueNoneText(
  plan: SchedulerRescuePlanDto,
  t: TFunction,
  stageLabel: RescueStageLabel,
  projectTitle?: string | null,
): string {
  const reason = plan.noneReason ?? 'NO_HARMLESS_OPTION';
  return t(key(`none.${reason}`), {
    project: projectTitle ?? '',
    stage: plan.noneStageCode ? stageLabel(plan.noneStageCode) : '',
  });
}

/**
 * The recommendation in one line — the strip's "Recomandare: …", the Gantt
 * icon's title, the chip's tooltip. READY: the first step that makes a
 * difference (and how many more) and where that lands the project — never an
 * anchor, the crew planned only because it would lead the operation anyway;
 * PENDING: still being worked out; NONE: why nothing helps (`projectTitle`
 * names the project there).
 *
 * Never starts with "Termen": the Gantt's deadline marker is found by that
 * title, and an icon sitting in the same row must not be mistaken for it.
 */
export function rescueSummaryText(
  plan: SchedulerRescuePlanDto,
  t: TFunction,
  stageLabel: RescueStageLabel,
  projectTitle?: string | null,
): string {
  if (plan.status === 'PENDING') return t(key('pending'));
  if (plan.status === 'NONE') return rescueNoneText(plan, t, stageLabel, projectTitle);
  const first =
    plan.steps.find((step) => !(step.kind === 'CREW' && step.anchor === true)) ?? plan.steps[0];
  const more = plan.steps.length - 1;
  const action = [
    first ? rescueShortStepText(first, t, stageLabel) : '',
    more > 0 ? t(key('moreSteps'), { count: more }) : '',
  ]
    .filter(Boolean)
    .join(' ');
  const after = plan.after;
  const date = formatDate(after?.projectedFinishAt);
  if (plan.outcome === 'ON_TIME') return t(key('summaryOnTime'), { action, date });
  return t(key('summaryLate'), {
    action,
    date,
    lateBy: formatMinutes(after?.lateByMinutes ?? 0),
    now: plan.before.blocked
      ? t(key('nowBlocked'))
      : t(key('nowLate'), { lateBy: formatMinutes(plan.before.lateByMinutes ?? 0) }),
  });
}

/** A step in full, as the dialog lists it. */
export function rescueStepText(
  step: SchedulerRescueStepDto,
  t: TFunction,
  stageLabel: RescueStageLabel,
): string {
  const stage = stageLabel(step.stageCode);
  if (step.kind === 'CREW') {
    if (step.mode === 'JOIN_NOW') return t(key('step.joinNow'), { team: step.teamName, stage });
    if (step.mode === 'MOVE_NOW') {
      return t(key('step.moveNow'), {
        team: step.teamName,
        from: step.from?.projectTitle ?? '',
        stage,
      });
    }
    // PLANNED: the crew takes the operation if it is the first free, else joins.
    return t(key(step.leads ? 'step.plannedLeads' : 'step.planned'), {
      team: step.teamName,
      stage,
      date: formatDate(step.joinsAt),
    });
  }
  if (step.kind === 'COLLABORATOR') {
    return t(key(step.leads ? 'step.collaboratorLeads' : 'step.collaboratorHelps'), {
      stage,
      date: formatDate(step.joinsAt),
    });
  }
  return t(key('step.shift'), {
    stage,
    teams: step.teams.map((team) => team.name).join(', '),
    hours: formatMinutes(step.minutesPerDay),
  });
}

/** What happens to another project's finish, in the dialog's "consequence" voice. */
function otherProjectText(
  other: {
    projectTitle: string;
    deadline: string | null;
    finishBefore: string | null;
    finishAfter: string | null;
    lateByAfter: number | null;
  },
  delayMinutes: number | null,
  t: TFunction,
): string {
  const project = other.projectTitle;
  if (other.deadline === null) {
    // No deadline to keep: it may move, and the manager is told by how much.
    return delayMinutes !== null && delayMinutes > 0
      ? t(key('consequence.noDeadline'), { project, delay: formatMinutes(delayMinutes) })
      : t(key('consequence.notLater'), { project, after: formatDateTime(other.finishAfter) });
  }
  // A deadline project is never made later than it was — late ones only say
  // so; on-time ones show they stay on time, and against which deadline.
  if ((other.lateByAfter ?? 0) > 0) {
    return t(key('consequence.notLater'), { project, after: formatDateTime(other.finishAfter) });
  }
  return t(key('consequence.onTime'), {
    project,
    before: formatDateTime(other.finishBefore),
    after: formatDateTime(other.finishAfter),
    deadline: formatDateTime(other.deadline),
  });
}

/** One entry of the "Alte proiecte" list: how much later another project finishes. */
export function rescueAffectedText(affected: SchedulerRescueAffectedDto, t: TFunction): string {
  return otherProjectText(affected, affected.delayMinutes, t);
}

/**
 * The muted line under a step: what it costs another project. Only a crew
 * moved off another project's job has one — the project it leaves carries on
 * with another crew or goes back to waiting, and finishes when. `affected`
 * (the plan's list) supplies how much later a deadline-less project finishes.
 */
export function rescueConsequenceText(
  step: SchedulerRescueStepDto,
  t: TFunction,
  context: {
    stageLabel?: RescueStageLabel;
    affected?: readonly SchedulerRescueAffectedDto[];
  } = {},
): string | null {
  if (step.kind !== 'CREW' || step.mode !== 'MOVE_NOW' || !step.from) return null;
  const from = step.from;
  const stageLabel = context.stageLabel ?? defaultStageLabel(t);
  const parts: string[] = [];
  if (from.outcome === 'REQUEUED') {
    parts.push(
      t(key('consequence.requeued'), {
        project: from.projectTitle,
        stage: stageLabel(from.stageCode),
      }),
    );
  } else if (from.outcome === 'PROMOTED' && from.promotedTeamName) {
    parts.push(
      t(key('consequence.promoted'), { project: from.projectTitle, team: from.promotedTeamName }),
    );
  }
  const delay =
    context.affected?.find((entry) => entry.schedulerProjectId === from.schedulerProjectId)
      ?.delayMinutes ?? null;
  parts.push(otherProjectText(from, delay, t));
  return parts.join(' · ');
}

/** A crew working short-handed (or not at all) over a run of days, and who is away. */
export function rescueLeaveText(
  leave: SchedulerRescueLeaveDto,
  t: TFunction,
  stageLabel: RescueStageLabel,
): string {
  const values = {
    team: leave.teamName,
    stage: stageLabel(leave.stageCode),
    from: formatCalendarDay(leave.from),
    to: formatCalendarDay(leave.to),
  };
  if (leave.present === 0) return t(key('leaveLineNobody'), values);
  return t(key('leaveLine'), {
    ...values,
    present: leave.present,
    total: leave.roster,
    names: leave.workerNames.join(', '),
  });
}

/**
 * Why a set of steps cannot be applied as previewed; null when nothing is
 * wrong. The most useful reason wins: the project no longer needs saving, then
 * what the manager's own edits caused and can undo (a name, the same
 * collaborator twice, a shift not above the current one, a shift left with no
 * internal crew, a Montaj that would outrun its painting), then "the board
 * moved on" for anything structural, and only then the ones about the outcome
 * (harm, no gain).
 *
 * `currentShiftMinutes`: the shifted operation's second shift as it is now —
 * what SHIFT_NOT_HIGHER has to be above.
 */
export function rescueProblemText(
  problems: readonly SchedulerRescueProblemDto[],
  t: TFunction,
  projectTitle?: string | null,
  context: { currentShiftMinutes?: number | null } = {},
): string | null {
  if (problems.length === 0) return null;
  const has = (code: SchedulerRescueProblemDto['code']) =>
    problems.find((problem) => problem.code === code);
  const notAtRisk = has('NOT_AT_RISK');
  if (notAtRisk) {
    return t(key('notAtRisk'), { project: notAtRisk.projectTitle ?? projectTitle ?? '' });
  }
  if (has('NAME_TAKEN')) return t(key('nameTaken'));
  if (has('DUPLICATE_STEP')) return t(key('collaboratorTwice'));
  if (has('SHIFT_NOT_HIGHER')) {
    return t(key('previewShiftNotHigher'), {
      hours: formatMinutes(context.currentShiftMinutes ?? 0),
    });
  }
  if (has('NO_INTERNAL_CREW')) return t(key('previewNoInternalCrew'));
  if (has('PAINTING_GATE')) return t(key('previewPaintingGate'));
  const outcomeCodes: ReadonlyArray<SchedulerRescueProblemDto['code']> = [
    'HARMS_OTHER_PROJECT',
    'NO_GAIN',
  ];
  if (problems.some((problem) => !outcomeCodes.includes(problem.code))) {
    return t(key('previewChanged'));
  }
  const harmed = has('HARMS_OTHER_PROJECT');
  if (harmed) return t(key('previewHarms'), { project: harmed.projectTitle ?? '' });
  return t(key('previewNoGain'));
}
